import { Typography } from "@/components/atoms";
import { CopyIcon, MapPinnedIcon } from "@/components/atoms/icons";
import { AdhaUnitStatusBadge, OkDialog } from "@/components/molecules";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { Toast } from "@/components/molecules/Toast";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { useContentfulRevision, useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { fetchPropertyImages } from "@/core/services/contentfulClient";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { useAppStore } from "@/core/stores/appStore";
import { shareFile } from "@/core/wrappers/sharing";
import {
  calculatePropertyOutstanding,
  formatDueDateWithPrefix,
  getPropertyInstallments,
  mapInstallmentStatusToPaymentStatus,
} from "@/modules/payments/utils/installmentUtils";
import { DOCUMENT_TYPES } from "@/modules/profile/constants/documentTypes";
import { downloadAndPersistDocument } from "@/modules/property-details/utils/documentDownload";
import * as Clipboard from "expo-clipboard";
import * as Linking from "expo-linking";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  ImageSourcePropType,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { AboutProperty } from "./components/AboutProperty";
import { PropertyDocumentsAccordion } from "./components/PropertyDocumentsAccordion";
import { PropertyOwnerDetailsAccordion } from "./components/PropertyOwnerDetailsAccordion";
import { PropertyPaymentsAccordion } from "./components/PropertyPaymentsAccordion";

export function PropertyDetailsScreen() {
  const colors = useUIColors();
  const { t, currentLanguage } = useTranslation();
  const { isRTL } = useI18nContext();
  const { propertyId } = useLocalSearchParams<{ propertyId?: string }>();

  // Dialog state
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // Toast state for copy feedback
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // Floor plan loading
  const [floorPlanLoading, setFloorPlanLoading] = useState(false);
  const [floorPlanSuccessVisible, setFloorPlanSuccessVisible] = useState(false);
  const [floorPlanSuccessMessage, setFloorPlanSuccessMessage] = useState("");
  const [floorPlanSavedUri, setFloorPlanSavedUri] = useState<string | null>(
    null,
  );

  // Property images state
  const [propertyImages, setPropertyImages] = useState<ImageSourcePropType[]>(
    [],
  );

  // Get property from store
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const user = useAppStore((state) => state.auth.user);

  const property = useMemo(() => {
    if (propertyId) {
      return properties.find((p) => p.id === propertyId);
    }
    return properties[0]; // Fallback to first property
  }, [propertyId, properties]);

  // Track property details view
  useEffect(() => {
    if (property) {
      trackDynatraceEvent({
        p_screen: "Property Details",
        p_actionName: "Property Details Viewed",
        p_sectionName: "Property Details",
        eventType: "view",
        propertyId: property.id,
        projectName: property.projectName || "",
        unitNumber: property.unitNumber || "",
        buildingName: property.buildingName || "",
      });
    }
  }, [property]);

  // Get installments for this property
  const installments = useMemo(() => {
    if (!property) return null;
    return getPropertyInstallments(unitInstallments, property.id);
  }, [property, unitInstallments]);

  // Calculate outstanding amount
  const outstandingAmount = useMemo(() => {
    return calculatePropertyOutstanding(installments);
  }, [installments]);

  // Calculate payment completion percentage
  // Formula: (paidAmount / totalPrice) * 100
  // Example: If paidAmount = 500,000 and totalPrice = 1,000,000, result = 50%
  const installmentPaidPercent = useMemo(() => {
    if (!property || property.totalPrice <= 0) return 0;
    return Math.round((property.paidAmount / property.totalPrice) * 100);
  }, [property]);

  // Check if any installment has cheque available
  const hasChequeAvailable = useMemo(() => {
    if (!installments) return false;

    const allInstallments = [
      ...installments.overdue,
      ...installments.upcomingDue,
      ...installments.future,
      ...installments.paid,
    ];

    return allInstallments.some((item) => item.has_cheque === true);
  }, [installments]);

  // Fetch property images from Contentful when property changes
  // Reload images after an app-wide Contentful refresh (app foreground).
  const contentfulRevision = useContentfulRevision();
  useEffect(() => {
    const loadPropertyImages = async () => {
      if (!property?.projectName) {
        setPropertyImages([]);
        return;
      }

      try {
        const images = await fetchPropertyImages(property.projectName, {
          maxWidth: 1200,
          locale: currentLanguage === "ar" ? "ar" : "en",
        });
        // Convert PropertyImage[] to ImageSourcePropType[]
        const imageSources: ImageSourcePropType[] = images.map((img) => ({
          uri: img.url,
        }));
        setPropertyImages(imageSources);
      } catch (error) {
        logger.error(
          "PropertyDetailsScreen",
          "Failed to fetch property images",
          {
            projectName: property.projectName,
            error,
          },
        );
        setPropertyImages([]);
      }
    };

    void loadPropertyImages();
  }, [property?.projectName, currentLanguage, contentfulRevision]);

  // Transform installments into payment items format
  const paymentItems = useMemo(() => {
    if (!installments) return [];

    // Helper function to translate payment type
    const translatePaymentType = (type: string): string => {
      if (type === null || type === undefined) return "";
      if (typeof type !== "string") return type as any;
      const typeLower = type.toLowerCase();
      if (typeLower.includes("handover")) return t("propertyDetails.handover");
      if (typeLower.includes("installment"))
        return t("propertyDetails.installment");
      if (typeLower.includes("down payment"))
        return t("propertyDetails.downPayment");
      if (typeLower.includes("adm fee") || typeLower.includes("adm"))
        return t("propertyDetails.admFee");
      return type; // Return original if no match
    };

    const allInstallments = [
      ...installments.overdue.map((item) => ({
        ...item,
        category: "overdue" as const,
      })),
      ...installments.upcomingDue.map((item) => ({
        ...item,
        category: "upcomingDue" as const,
      })),
      ...installments.future.map((item) => ({
        ...item,
        category: "future" as const,
      })),
      ...installments.paid.map((item) => ({
        ...item,
        category: "paid" as const,
      })),
    ];

    return allInstallments.map((item) => ({
      id: item.id,
      title: translatePaymentType(item.type || item.payment_name),
      // For items that need payment (overdue, upcoming, future), show due_amount
      // For paid items, show paid_amount or amount
      amount:
        item.category === "paid"
          ? item.paid_amount || item.amount || 0
          : item.due_amount || 0,
      // Backend only provides due_date; use "Due date" label for paid items, and "Due by" for others
      dueDate: formatDueDateWithPrefix(
        item.due_date,
        item.category === "paid"
          ? t("payments.dueDate")
          : t("propertyDetails.dueBy"),
      ),
      status: mapInstallmentStatusToPaymentStatus(item.category, item.status),
      // Both 'installment' and 'other_charge' are property payments
      type: (item.payment_type === "installment" ||
      item.payment_type === "other_charge"
        ? "property"
        : "community") as "property" | "community",
    }));
  }, [installments, t]);

  if (!property) {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <TextHeader title={t("propertyDetails.title")} />
        <View style={styles.scrollContent}>
          <Typography variant="body" color="primary">
            {t("propertyDetails.noPropertyData")}
          </Typography>
        </View>
      </View>
    );
  }

  const handleViewFloorPlan = async () => {
    try {
      if (!property?.id || !user?.customer_id) {
        throw new Error("Property or customer unavailable");
      }

      setFloorPlanLoading(true);

      const downloadResult = await downloadAndPersistDocument({
        docType: DOCUMENT_TYPES.FLOOR_PLAN as any,
        parentId: property.id, // unit id
        customerId: user.customer_id,
        translationKey: "propertyDetails.viewFloorPlan",
        t: t as any,
      });

      if (
        downloadResult.success &&
        downloadResult.fileUri &&
        downloadResult.message
      ) {
        setFloorPlanSuccessMessage(downloadResult.message);
        setFloorPlanSavedUri(downloadResult.fileUri);
        setFloorPlanSuccessVisible(true);
      } else {
        throw new Error(downloadResult.error || "Failed to save floor plan");
      }
    } catch (error: any) {
      logger.error("PropertyDetailsScreen", "View floor plan failed", {
        error: error?.message || error,
      });
      setDialogMessage(error?.message || "Unable to open floor plan");
      setDialogVisible(true);
    } finally {
      setFloorPlanLoading(false);
    }
  };

  const handlePaySelected = (selectedPaymentIds: string[]) => {
    // Navigate to property payments screen with selected payments
    router.push(
      `/(auth)/property-payments?tab=upcoming&selectedPaymentIds=${selectedPaymentIds.join(",")}` as any,
    );
  };

  const handleViewAll = (tab: "upcoming" | "history") => {
    // Navigate to property payments screen with the appropriate tab
    router.push(`/(auth)/property-payments?tab=${tab}` as any);
  };

  const handleMapPress = () => {
    if (!property.latitude || !property.longitude) {
      logger.warn("PropertyDetailsScreen:No coordinates available for map");
      setDialogMessage(
        "Location coordinates are not available for this property.",
      );
      setDialogVisible(true);
      return;
    }

    const { latitude, longitude } = property;
    let mapUrl: string;

    if (Platform.OS === "ios") {
      // iOS: Use Apple Maps
      mapUrl = `http://maps.apple.com/?q=${latitude},${longitude}`;
    } else if (Platform.OS === "android") {
      // Android: Use Google Maps
      mapUrl = `geo:${latitude},${longitude}?q=${latitude},${longitude}`;
    } else {
      // Web: Use Google Maps web
      mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    }

    Linking.openURL(mapUrl).catch((err) => {
      logger.error("PropertyDetailsScreen:Failed to open maps", err);
      setDialogMessage("Failed to open maps application.");
      setDialogVisible(true);
    });
  };

  const handleCopyAddressingNumber = async () => {
    if (!property.addressingNumber) {
      logger.warn("PropertyDetailsScreen:No addressing number to copy");
      return;
    }

    try {
      await Clipboard.setStringAsync(property.addressingNumber);
      logger.info(
        "PropertyDetailsScreen:Addressing number copied to clipboard",
        {
          addressingNumber: property.addressingNumber,
        },
      );

      // Show toast feedback
      setToastMessage(t("propertyDetails.addressingNumberCopied"));
      setToastVisible(true);
    } catch (error) {
      logger.error(
        "PropertyDetailsScreen:Failed to copy addressing number",
        error,
      );
      setDialogMessage(t("propertyDetails.failedToCopy"));
      setDialogVisible(true);
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader title={t("propertyDetails.title")} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Image Carousel */}
        {property && (
          <ImageCarousel
            images={propertyImages}
            height={230}
            showNavigationButtons
            {...(property.handoverComplete && { constructionCompleted: 100 })}
          />
        )}

        {/* Property Title and Location */}
        {property && (
          <View style={styles.propertyHeader}>
            {/* Property address with map icon */}
            <Pressable
              style={styles.addressRow}
              onPress={handleMapPress}
              android_ripple={{ color: colors.backgroundLevel2 }}
            >
              <Typography variant="title3" color="primary">
                {property.unitNumber || property.projectName || "Property"}
              </Typography>
              <MapPinnedIcon size={24} color={colors.fontPrimary} />
            </Pressable>

            {/* Addressing number */}
            {property.addressingNumber && (
              <Pressable
                style={styles.addressingRow}
                onPress={handleCopyAddressingNumber}
                android_ripple={{ color: colors.backgroundLevel2 }}
              >
                <Typography variant="body" color="primary">
                  {t("propertyDetails.addressingNumber")}{" "}
                </Typography>
                <Typography variant="headline" color="primary">
                  {property.addressingNumber}
                </Typography>
                <CopyIcon size={16} color={colors.fontPrimary} />
              </Pressable>
            )}

            {/* SPA availability */}
            <Typography
              variant="body"
              color="secondary"
              style={isRTL ? styles.spaAvailableRTL : undefined}
            >
              {t("propertyDetails.spaAvailable")}
            </Typography>
            {property.isADHAUnit ? (
              <AdhaUnitStatusBadge rawStatus={property.adhaUnitStatus} />
            ) : null}
          </View>
        )}

        {/* Payment Summary Card */}
        {property && (
          <View
            style={[
              styles.summaryCard,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            {/* Progress bar: Green fills based on payment completion percentage
                            - LTR: Fills from left (0% = no green, 100% = full green)
                            - RTL: Fills from right (0% = no green, 100% = full green)
                            Background color shows remaining percentage */}
            <View
              style={[
                styles.progressBackground,
                {
                  width: `${installmentPaidPercent}%`,
                  ...(isRTL
                    ? { right: 0, left: "auto" }
                    : { left: 0, right: "auto" }),
                },
              ]}
            />

            {/* Left section: Payment completion percentage */}
            <View style={styles.summaryLeft}>
              <View style={styles.summaryLeftContent}>
                <Typography variant="caption" color="secondary">
                  {t("propertyDetails.paymentCompleted")}
                </Typography>
                <Typography variant="headline" color="primary">
                  {installmentPaidPercent}%
                </Typography>
              </View>
            </View>

            {/* Right section: Outstanding amount */}
            <View style={styles.summaryRight}>
              <Typography variant="caption" color="secondary">
                {t("propertyDetails.amountOutstanding")}
              </Typography>
              <Typography variant="headline" color="primary">
                {outstandingAmount.toLocaleString()}
              </Typography>
            </View>
          </View>
        )}

        {/* About Property Component */}
        {property && (
          <AboutProperty
            bedrooms={property.bedrooms || 0}
            bathrooms={property.bathrooms || 0}
            areaSqFt={property.area || 0}
            amenities={property.amenities || []}
            onViewFloorPlan={handleViewFloorPlan}
            floorPlanLoading={floorPlanLoading}
          />
        )}

        {/* Documents Component */}
        {property && (
          <PropertyDocumentsAccordion
            salesOrderId={property.salesOrderId}
            unitId={property.id}
            documents={(property as any).documents || []}
            unitLabel={[property.unitNumber, property.projectName]
              .filter(Boolean)
              .join(" ")}
          />
        )}

        {/* Owner Details Component */}
        {property?.buyerName ? (
          (() => {
            // Calculate main user's share (100% - sum of all joint owner shares)
            const jointOwnersTotalShare = (property.jointOwners || []).reduce(
              (sum, owner) => sum + owner.share,
              0,
            );
            const mainUserShare = Math.max(0, 100 - jointOwnersTotalShare);

            // Build owners list: main user first (primary), then joint owners
            const owners = [
              // Main user (primary owner)
              {
                id: "main-owner",
                name: property.buyerName,
                share: mainUserShare,
                isPrimary: true,
              },
              // Joint owners
              ...(property.jointOwners || []).map((owner, index) => ({
                id: `joint-owner-${index}`,
                name: owner.name,
                share: owner.share,
                isPrimary: false,
              })),
            ];

            return <PropertyOwnerDetailsAccordion owners={owners} />;
          })()
        ) : null}

        {/* Payments Component */}
        <PropertyPaymentsAccordion
          payments={paymentItems as any}
          onlinePaymentEnabled={property.isOnlinePaymentEnabled === true}
          onPaySelected={handlePaySelected}
          onViewAll={handleViewAll}
        />

        {/* Cheque Available Info temporarily disabled per request */}
        {/* {hasChequeAvailable && (
                    <View style={[styles.infoCard, { backgroundColor: colors.backgroundLevel1, borderColor: colors.backgroundLevel2 }]}>
                        <CircleAlertIcon size={24} color={colors.fontPrimary} />
                        <View style={styles.infoCardContent}>
                            <Typography variant="headline" color="primary" style={styles.chequeTitle}>
                                {t('propertyDetails.chequeAvailable')}
                            </Typography>
                            <Typography variant="body" color="primary" style={styles.chequeDescription}>
                                {t('propertyDetails.chequeAvailableDescription')}
                            </Typography>
                            <Button
                                title={t('propertyDetails.raiseRequest')}
                                variant="text"
                                onPress={() => {
                                    router.push({
                                        pathname: '/service-request-create',
                                        params: {
                                            categoryName: 'Replace My Cheque',
                                            description: 'Raise cheque request',
                                            unitId: property.id,
                                            unitLabel: [property.unitNumber, property.projectName].filter(Boolean).join(' '),
                                        },
                                    } as any);
                                }}
                                style={styles.raiseRequestButton}
                            />
                        </View>
                    </View>
                )} */}

        {/* Bottom padding - Independent of cheque card */}
        <View style={styles.bottomPadding} />
      </ScrollView>
      <OkDialog
        visible={dialogVisible}
        message={dialogMessage}
        onOk={() => setDialogVisible(false)}
      />
      <OkDialog
        visible={floorPlanSuccessVisible}
        title={t("common.downloadSuccessTitle")}
        message={floorPlanSuccessMessage}
        okText={Platform.OS === "ios" ? t("common.saveShare") : t("common.ok")}
        onOk={async () => {
          if (Platform.OS === "ios" && floorPlanSavedUri) {
            await shareFile({ uri: floorPlanSavedUri });
          }
          setFloorPlanSavedUri(null);
          setFloorPlanSuccessVisible(false);
        }}
      />
      <Toast
        visible={toastVisible}
        message={toastMessage}
        onHide={() => setToastVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  propertyHeader: {
    gap: 8,
  },
  addressRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  addressingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  summaryCard: {
    flexDirection: "row",
    borderRadius: 12,
    overflow: "hidden",
    position: "relative", // For absolute positioning of progress
  },
  progressBackground: {
    position: "absolute",
    top: 0,
    bottom: 0,
    height: "100%",
    backgroundColor: "#81c784", // Green progress indicator
  },
  summaryLeft: {
    width: 191,
    paddingHorizontal: 12,
    paddingVertical: 12,
    paddingRight: 8,
    position: "relative", // On top of progress
    zIndex: 1,
  },
  summaryLeftContent: {
    gap: 2,
  },
  summaryRight: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 12,
    paddingLeft: 8,
    paddingRight: 12,
    gap: 2,
    alignItems: "flex-end",
    position: "relative", // On top of progress
    zIndex: 1,
  },
  section: {
    borderRadius: 12,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#ededed",
  },
  documentsList: {
    padding: 16,
    gap: 12,
  },
  documentItem: {
    paddingVertical: 8,
    gap: 4,
  },
  coOwnersList: {
    padding: 16,
    gap: 12,
  },
  coOwnerItem: {
    paddingVertical: 8,
    gap: 4,
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    borderWidth: 1,
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  infoCardContent: {
    flex: 1,
    gap: 10,
  },
  raiseRequestButton: {
    alignSelf: "flex-start",
    paddingVertical: 12,
    paddingHorizontal: 0,
    height: 47,
  },
  chequeTitle: {
    fontSize: 16,
    fontWeight: "600",
    lineHeight: 16,
  },
  chequeDescription: {
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 16,
  },
  bottomPadding: {
    height: 24,
  },
  spaAvailableRTL: {
    textAlign: "right",
  },
});

export default PropertyDetailsScreen;
